'use strict';

import BreadcrumbStore from './stores/BreadcrumbStore';
import BrowserStore from './stores/BrowserStore';
import ConversionStore from './stores/ConversionStore';
import FavoriteStore from './stores/FavoriteStore';
import ActorStore from './stores/ActorStore';
import GuestStore from './stores/GuestStore';
import LoggerStore from './stores/LoggerStore';
import TrackerStore from './stores/TrackerStore';
import MessengerActorStore from './stores/MessengerActorStore';
import MessengerChannelsStore from './stores/MessengerChannelsStore';
import MessengerStore from './stores/MessengerStore';
import NotificationStore from './stores/NotificationStore';
import SpecialStore from './stores/SpecialStore';
import TeaserStore from './stores/TeaserStore';
import TileGridStore from './stores/TileGridStore';
import VXMobileStore from './stores/VXMobileStore';
import ActionCreator from './actions/ActionCreator';
import ActorActionCreator from './actions/ActorActionCreator';
import ChatExitCodes from './ChatExitCodes';
import Constants from './Constants';
import GalleryStore from './stores/GalleryStore';
import TVMediaStore from './stores/TVMediaStore';
import ContestStore from './stores/ContestStore';
import BlogPostsStore from './stores/BlogPostsStore';
import MessengerGiftStore from './stores/MessengerGiftStore';
import MessengerToyControlStore from './stores/MessengerToyControlStore';
import VxqlStore from './stores/VxqlStore';
import VideoJumbotronStore from './stores/VideoJumbotronStore';
import FooterLinksStore from './stores/FooterLinksStore';
import BlogPostsActionCreator from './actions/BlogPostsActionCreator';
import GlobalSearchStore from './stores/GlobalSearchStore';
import SearchStore from './stores/SearchStore';
import DailyBonusStore from './stores/DailyBonusStore';
import DailyBonusActionCreator from './actions/DailyBonusActionCreator';
import VideoV2Store from './stores/VideoV2Store';
import ActorsStore from './stores/ActorsStore';
import StoreFactory from './stores/StoreFactory';
import TagsStore from './stores/TagsStore';
import TVSeriesStore from './stores/TVSeriesStore';
import FeedStore from './stores/FeedStore';
import SlotmachineStore from './stores/SlotmachineStore';
import VideoChatbuttonStore from './stores/VideoChatbuttonStore';
import TopCamStore from './stores/TopCamStore.js';
import VideoStarsStore from './stores/VideoStarsStore.js';
import BlogActorsStore from './stores/BlogActorsStore.js';

const stores = {
	MESSENGER: 'messenger',
	GIFT: 'gift',
	TOYCONTROL: 'toyControl',
	NOTIFICATION: 'notification',
};

const initHelper = (function () {
	const storesReady = {};

	const onReadyListeners = {};

	return {
		isReady: function (store) {
			return typeof storesReady[store] === 'undefined' ? false : storesReady[store];
		},

		setReady: function (store) {
			storesReady[store] = true;
			this.callOnReadyListeners(store);
		},

		addReadyListener: function (store, listener) {
			if (typeof listener === 'function') {
				if (typeof onReadyListeners[store] === 'undefined') {
					onReadyListeners[store] = []; // init onReadyListeners
				}
				onReadyListeners[store].push(listener);
				this.callOnReadyListeners(store);
			}
		},

		callOnReadyListeners: function (store) {
			if (this.isReady(store)) {
				if (typeof onReadyListeners[store] !== 'undefined') {
					for (const listener of onReadyListeners[store]) {
						listener();
					}
				}
				onReadyListeners[store] = undefined;
			}
		},
	};
})();

const Flux = {
	Logger: LoggerStore,
	Button: VideoChatbuttonStore,
	Slotmachine: SlotmachineStore,
	TopCams: TopCamStore,
	FooterLinks: FooterLinksStore,
	VXGame: VxqlStore.VXGame,
	Search: SearchStore,
	GlobalSearch: GlobalSearchStore,
	Tags: TagsStore,
	Tracker: TrackerStore,
	Breadcrumb: BreadcrumbStore,
	TVSeries: TVSeriesStore,
	Special: SpecialStore,
	Gallery: GalleryStore,
	TVMedia: TVMediaStore,
	VXMobile: VXMobileStore,
	Feed: FeedStore,
	Conversion: ConversionStore,
	VideoV2: VideoV2Store,
	Actors: ActorsStore,
	MessengerContent: MessengerStore,

	Global: {
		clearCache: function () {
			ActorStore.clearCache();
			VideoV2Store.clearCache();
			GalleryStore.clearCache();
			SearchStore.clearCache();
		},
	},

	Vxql: Object.assign(VxqlStore, {
		loadHeatMaps: function (actorId, callback) {
			VxqlStore.getActorHeatMaps(actorId).then((result) => {
				const chainIsValid = result && result.data && result.data.model && result.data.model.heatMaps;
				const heatMap = chainIsValid ? result.data.model.heatMaps.heatMap : [];
				const hoursToBeOnline = chainIsValid ? result.data.model.heatMaps.hoursToBeOnline : null;
				const actorName = chainIsValid ? result.data.model.actorName : '';
				const memo = chainIsValid ? result.data.model.heatMaps.memo : null;
				const errors = result && result.errors ? result.errors : [];
				ActorStore.storeActorHeatMaps(actorId, heatMap, hoursToBeOnline, actorName, memo, errors);

				callback(actorId); // Notify client that it's time to go for a cache into ActorStore

				return result;
			});
		},
	}),

	Browser: Object.assign(BrowserStore, {
		setShouldModalWindowClose: function () {
			BrowserStore.closeModalWindow();
		},
		setSupportChatHidden: function () {
			BrowserStore.setLocalItem('supportChat', true);
		},
		updateExpireStamp: function (ttl) {
			if (ttl > 0) {
				BrowserStore.setLocalItem('expat', Date.now() + ttl * 1000);
			}
		},
		checkExpireStamp: function () {
			const expireStamp = parseInt(BrowserStore.getLocalItem('expat'));
			const result = expireStamp && Date.now() > expireStamp;

			if (result) {
				BrowserStore.removeItem('expat');
			}

			return result;
		},
		isTestDevice: function () {
			return window.location.search && window.location.search.indexOf('testdevice') > -1;
		},
	}),

	Actor: Object.assign(ActorStore, {
		getActorNote: function (actorId, storeAutoOpenSetting = false) {
			if (storeAutoOpenSetting && actorId) {
				BrowserStore.setLocalItem('NoteactorId', actorId);
			}
			return ActorStore._getActorNote(actorId);
		},
		getProfileLiveShowTicket: (actorId, callback) => VxqlStore.getProfileLiveShowTicket(actorId, callback),
		loadChatRatings: function (actorId) {
			ActorActionCreator.loadActorChatRatings(actorId);
		},
		loadActorInterviews: (
			actorId,
			imageSize = Constants.Interviews.IMAGE_SIZE_720,
			placeholder = true,
			durationFormat = Constants.Interviews.DURATION_SEC,
		) => ActorActionCreator.loadActorInterviews(actorId, imageSize, placeholder, durationFormat),
		loadModelRecommendations: (actorId, first = 4, isExtended = false, ageRating = 16) =>
			ActorActionCreator.loadModelRecommendations(actorId, first, isExtended, ageRating),
	}),

	Guest: Object.assign(GuestStore, {
		isPremium: function () {
			return GuestStore.isVXTVAboSubscriber() || GuestStore.isVip();
		},

		addVideoLike: function (id, callback) {
			GuestStore.setVideoLike(id);
			VxqlStore.addVideoLike(id, callback);
		},

		addVideoDislike: function (id, callback) {
			GuestStore.setVideoDislike(id);
			VxqlStore.addVideoDislike(id, callback);
		},

		addAlbumView: function (id) {
			Flux.Guest.isLoggedIn() && VxqlStore.addAlbumView(id);
		},

		addMabAlbumView: function (id) {
			Flux.Guest.isLoggedIn() && VxqlStore.addMabAlbumView(id);
		},

		addGalleryLike: function (id, callback) {
			VxqlStore.addGalleryLike(id, callback);
		},

		addGalleryDislike: function (id, callback) {
			VxqlStore.addGalleryDislike(id, callback);
		},

		/**
		 *
		 * @param {string} code
		 * @param {function} callback
		 */
		getBonusCode: function (code, callback) {
			return VxqlStore.getBonusCode(code, callback);
		},

		/**
		 *
		 * @param {function} callback
		 */
		getRemainingBonusCodes: function (callback) {
			return VxqlStore.getRemainingBonusCodes(callback);
		},

		getGuestVideoTime: function (chatId, callback) {
			VxqlStore.getGuestVideoTime(chatId, callback);
		},

	}),

	NewMessenger: Object.assign(MessengerStore, {
		setIsChatExitOpen: function (isOpen) {
			ActionCreator.Messenger.setIsChatExitOpen(isOpen);
		},
		buyChatTicket: function (ticket, creationType = Flux.Constants.TicketCreationType.CHAT_TICKET) {
			ActionCreator.Messenger.buyChatTicket(ticket, creationType);
		},
		removeChatTicket: function (ticket) {
			ActionCreator.Messenger.removeChatTicket(ticket);
		},
	}),

	Notification: Object.assign(NotificationStore, {
		init: function (url) {
			if (!initHelper.isReady(stores.NOTIFICATION)) {
				NotificationStore._init(url);
				initHelper.setReady(stores.NOTIFICATION);
			}
		},
		onReady: function (listener) {
			initHelper.addReadyListener(stores.NOTIFICATION, listener);
		},
		chargeOneClick: function (amount) {
			ActionCreator.Notification.chargeOneClick(amount, MessengerChannelsStore.getSelectedChannelId());
		},
		archiveChannel: function (channelId) {
			ActionCreator.Notification.archiveChannel(channelId, true);
		},
		unarchiveChannel: function (channelId) {
			ActionCreator.Notification.archiveChannel(channelId, false);
		},
		markNavbarNotificationAsRead: function (notificationId) {
			const found = NotificationStore._markNavbarNotificationAsRead(notificationId);
			if (found) {
				ActionCreator.Notification.markNavbarNotificationAsRead(notificationId);
			}
		},
		markNavbarNotificationAsSeen: function (notificationId) {
			const found = NotificationStore._markNavbarNotificationAsSeen(notificationId);
			if (found) {
				ActionCreator.Notification.markNavbarNotificationAsSeen(notificationId);
			}
		},
		markAllNavbarNotificationsAsSeen: function () {
			const notificationsIds = NotificationStore._markAllNavbarNotificationsAsSeen();
			if (notificationsIds.length) {
				for (let i = 0; i < notificationsIds.length; i++) {
					const notificationId = notificationsIds[i];
					ActionCreator.Notification.markNavbarNotificationAsSeen(notificationId);
				}
			}
		},
		markAllNavbarNotificationsAsRead: function () {
			const notificationsIds = NotificationStore._markAllNavbarNotificationsAsRead();
			if (notificationsIds.length) {
				for (let i = 0; i < notificationsIds.length; i++) {
					const notificationId = notificationsIds[i];
					ActionCreator.Notification.markNavbarNotificationAsRead(notificationId);
				}
			}
		},
		markFloatingNotificationAsRead: function (notificationId) {
			const found = NotificationStore._markFloatingNotificationAsRead(notificationId);
			if (found) {
				ActionCreator.Notification.markFloatingNotificationAsRead(notificationId);
			}
		},
		refreshEmailProperties: function () {
			ActionCreator.Notification.refreshEmailProperties();
		},
		passwordChanged: function () {
			ActionCreator.Notification.passwordChanged();
		},
		sendSignupEmail: function (email) {
			ActionCreator.Notification.sendSignupEmail(email);
		},
		sendEmailBouncedResetMail: function (email) {
			ActionCreator.Notification.sendEmailBouncedResetMail(email);
		},
		removeStatusBar: function () {
			ActionCreator.Notification.removeStatusBar();
		},
	}),

	Teaser: Object.assign(TeaserStore, {
		getTeasers: function () {
			return TeaserStore._getTeasers(Flux.VXMobile.getRouteName(), Flux.VXMobile.getRouteArgs());
		},
		hasTeasers: function () {
			return TeaserStore._hasTeasers(Flux.VXMobile.getRouteName(), Flux.VXMobile.getRouteArgs());
		},
	}),

	TileGrid: Object.assign(TileGridStore, {
		loadGrid: function (type, payload, useInfiniteScrolling) {
			ActionCreator.TileGrid.loadGrid(type, payload, useInfiniteScrolling);
		},
		loadVideoAlbumPictures: function (albumId) {
			ActionCreator.TileGrid.loadVideoAlbumPictures(albumId);
		},
		setScrollPosition: function (tileGridId, offset) {
			ActionCreator.TileGrid.setScrollPosition(tileGridId, offset);
		},
		setBackLink: function (tileGridId) {
			ActionCreator.TileGrid.setBackLink(tileGridId);
		},
		resetScrollPosition: function (tileGridId) {
			ActionCreator.TileGrid.resetScrollPosition(tileGridId);
		},
		resetBackLink: function (tileGridId) {
			ActionCreator.TileGrid.resetBackLink(tileGridId);
		},
		setRestoreScrollPosition: function (restorePosition) {
			ActionCreator.TileGrid.setRestoreScrollPosition(restorePosition);
		},
		setTileGridKey: function (tileGridId, tileGridKey) {
			ActionCreator.TileGrid.setTileGridKey(tileGridId, tileGridKey);
		},
		setTileGridMode: function (tileGridId, gridMode) {
			ActionCreator.TileGrid.setTileGridMode(tileGridId, gridMode);
		},
	}),

	Contest: Object.assign(ContestStore, {
		getVxqlContestsFromStore: function () {
			return VxqlStore.getVxqlContestsFromStore();
		},
		getVxqlContestsFromApi: function (activeIsSpecial, callback) {
			VxqlStore.getVxqlContestsFromApi(activeIsSpecial, callback);
		},
		getVotingModule: function (votingId, callback) {
			VxqlStore.getContestVotingModule(votingId, callback);
		},
	}),

	Blog: Object.assign(BlogPostsStore, {
		requestPosts(category, page = 1, pageSize = Constants.Blog.PageSize.default) {
			BlogPostsActionCreator.dispatchGetBlogPostsAction(category, page, pageSize);
		},
		getActorsByIds: function (ids) {
			return BlogActorsStore.getActorsByIds(ids);
		},
	}),

	Gift: Object.assign(MessengerGiftStore, {
		init: function () {
			if (!Flux.Gift.isInitialized()) {
				MessengerGiftStore._init();
				initHelper.setReady(stores.GIFT);
			}
		},
		isInitialized: function () {
			return initHelper.isReady(stores.GIFT);
		},
		addGiftToMessage: function (giftId) {
			ActionCreator.Messenger.addGiftToMessage(giftId);
		},
		removeGiftFromMessage: function (giftId) {
			ActionCreator.Messenger.removeGiftFromMsg(giftId);
		},
	}),

	ToyControl: Object.assign(MessengerToyControlStore, {
		init: function () {
			if (!Flux.ToyControl.isInitialized()) {
				MessengerToyControlStore._init();
				initHelper.setReady(stores.TOYCONTROL);
			}
		},
		isInitialized: function () {
			return initHelper.isReady(stores.TOYCONTROL);
		},
	}),

	Video: {
		Jumbotron: Object.assign(VideoJumbotronStore, {
			loadConfig: function (type = Constants.Video.Jumbotron.TYPE_VIDEO_STATION) {
				VideoJumbotronStore.requestConfig(type);
			},
		}),
		VideoStars: VideoStarsStore,
	},

	DailyBonus: Object.assign(DailyBonusStore, {
		getDailybonusFromStore: function (date) {
			return VxqlStore.getDailybonusFromStore(date);
		},
		getDailyBounesFromApi: function (date, callback) {
			VxqlStore.getDailyBounesFromApi(date, callback);
		},
		triggerLoad: function () {
			DailyBonusActionCreator.reload();
		},
		loadInteractiveContent: function (limit, order) {
			DailyBonusActionCreator.loadInteractiveContent(limit, order);
		},
		requestEventConfig: function (date, isMobile = false) {
			DailyBonusActionCreator.requestEventConfig(date, isMobile);
		},
	}),

	Favorite: Object.assign(FavoriteStore, {
		setPinnedActorEmailNotification: function (actorId, enabled) {
			ActionCreator.Notification.setPinnedActorEmailNotification(actorId, enabled);
		},
		setPinnedActorSMSNotification: function (actorId, enabled) {
			ActionCreator.Notification.setPinnedActorSMSNotification(actorId, enabled);
		},
		pinActor: function (actorId) {
			ActionCreator.Notification.pinActor(actorId);
		},
		unpinActor: function (actorId) {
			ActionCreator.Notification.unpinActor(actorId);
		},
		pinAlbum: function (albumId) {
			ActionCreator.Notification.pinAlbum(albumId);
		},
		unpinAlbum: function (albumId) {
			ActionCreator.Notification.unpinAlbum(albumId);
		},
		pinSedcards: function (actorId) {
			ActionCreator.Notification.pinSedcards(actorId);
		},
		pinPicture: function (pictureId) {
			ActionCreator.Notification.pinPicture(pictureId);
		},
		unpinPicture: function (pictureId) {
			ActionCreator.Notification.unpinPicture(pictureId);
		},
	}),

	// TODO Merge MessengerActorStore into one MessengerChannelsStore
	Messenger: Object.assign(MessengerChannelsStore, {
		init: function (language, channels, channelsFilteredBy, showOnline, navbarMessages) {
			if (!Flux.Messenger.isInitialized()) {
				MessengerChannelsStore._init(channels, navbarMessages, channelsFilteredBy, showOnline);
				MessengerActorStore.init(language);
				initHelper.setReady(stores.MESSENGER);
			}
		},

		isInitialized: function () {
			return initHelper.isReady(stores.MESSENGER);
		},

		addActorChangeListener: function (callback) {
			MessengerActorStore.addEventListener(MessengerActorStore.EVENTS.actor, callback);
		},

		removeActorChangeListener: function (callback) {
			MessengerActorStore.removeEventListener(MessengerActorStore.EVENTS.actor, callback);
		},

		/**
		 * add on ready listener
		 */
		onReady: function (listener) {
			initHelper.addReadyListener(stores.MESSENGER, listener);
		},

		// actions

		setAllMessagesRead: function () {
			ActionCreator.Messenger.setAllMessagesRead();
		},

		setRestorePreviousUrl: function (restorePreviousUrl) {
			ActionCreator.Messenger.setRestorePreviousUrl(restorePreviousUrl);
		},

		loadMoreChannels: function (limit) {
			const channels = MessengerChannelsStore.getChannels();
			const offset = channels ? channels.length : 0;
			ActionCreator.Notification.loadMoreChannels(limit, offset);
		},

		loadOnlineChannels: function (online) {
			ActionCreator.Notification.loadOnlineChannels(online);
		},

		filterChannels: function (filterType) {
			ActionCreator.Notification.filterChannels(filterType);
		},

		filterOnlineChannels: function (active) {
			ActionCreator.Messenger.filterOnlineChannels(active);
		},

		updateRecommendedActors: function (currentActorId, fsk) {
			ActionCreator.Messenger.updateRecommendedActors(currentActorId, fsk);
		},

		updateRecommendedActorsGrid: function (currentActorId) {
			ActionCreator.Messenger.updateRecommendedActorsGrid(currentActorId);
		},

		selectChannelById: function (channelId, storeAutoOpenSetting) {
			ActionCreator.Messenger.selectChannelById(channelId);

			if (storeAutoOpenSetting && channelId && Flux.Guest.isLoggedIn()) {
				BrowserStore.setLocalItem('channelId', channelId);
			}
		},

		selectChannelByActorId: function (actorId, storeAutoOpenSetting, touchChannel, isRestored, shouldSelect) {
			ActionCreator.Messenger.selectChannelByActorId(actorId, touchChannel, isRestored, shouldSelect);

			if (storeAutoOpenSetting && actorId && Flux.Guest.isLoggedIn()) {
				BrowserStore.setLocalItem('actorId', actorId);
			}
		},

		provideChannelByActorId: function (actorId) {
			ActionCreator.Messenger.selectChannelByActorId(actorId, null, null, false);
		},

		restoreChannel: function (touchChannel) {
			const channelId = BrowserStore.getLocalItem('channelId');
			const actorId = BrowserStore.getLocalItem('actorId');

			if (channelId) {
				ActionCreator.Messenger.selectChannelById(channelId, touchChannel, true);
			} else if (actorId) {
				ActionCreator.Messenger.selectChannelByActorId(actorId, touchChannel, true);
			}
		},

		clearChannel: function () {
			BrowserStore.setLocalItem('channelId', '');
			BrowserStore.setLocalItem('actorId', '');
		},

		setAllChannelsSeen: function () {
			ActionCreator.Notification.setAllChannelsSeen();
		},

		unselectChannel: function () {
			ActionCreator.Messenger.unselectChannel();
		},

		sendMessage: function (channelId, senderType, messageType, isVideochat, isFreechat, data, includedGifts = [], chatId = 0) {
			ActionCreator.Notification.sendMessage(channelId, senderType, messageType, isVideochat, isFreechat, data, includedGifts, chatId);
		},

		setUnreadMessagesCount: function (unreadCount) {
			ActionCreator.Messenger.setUnreadMessagesCount(unreadCount);
		},

		// getter

		getActor: function () {
			return MessengerActorStore.getActor();
		},

		updateActor: function (actorId) {
			return MessengerActorStore.updateActor(actorId);
		},

		getMessengerGifts: function (callback) {
			MessengerStore.getMessengerGifts(callback);
		},

		getMessengerToyControls: function (callback) {
			MessengerStore.getMessengerToyControls(callback);
		},
	}),

	// USAGE OF FACTORY
	ProSearch: {
		loadPage: function (page, searchType, append = false) {
			StoreFactory.getProSearchMetaStore().then((store) => store.loadPage(page, searchType, append));
		},

		changeSearchCondition: function (field, value, valueTranslation, checked, searchType, doSearch = true) {
			StoreFactory.getProSearchMetaStore().then((store) =>
				store.changeSearchCondition(field, value, valueTranslation, checked, searchType, doSearch),
			);
		},

		deleteFilter: function (searchType) {
			StoreFactory.getProSearchMetaStore().then((store) => store.deleteFilter(searchType));
		},

		setActorOnlineState: function (actorId, state) {
			StoreFactory.getProSearchMetaStore().then((store) => store.setActorOnlineState(actorId, state));
		},

		setSearchLocationByZip(zipCode) {
			StoreFactory.getProSearchMetaStore().then((store) => store.setSearchLocationByZip(zipCode));
		},

		setSearchLocationToPrevious() {
			StoreFactory.getProSearchMetaStore().then((store) => store.setSearchLocationToPrevious());
		},
	},
	LiveReactions: {
		addLiveReaction: function (callback, referenceType, referenceId, imageId, message, data) {
			StoreFactory.getLiveReactionsStore().then((store) => {
				store.addReaction(callback, referenceType, referenceId, imageId, message, data);
			});
		},

		handleLiveReaction: function (liveReaction) {
			StoreFactory.getLiveReactionsStore().then((store) => {
				store.handleLiveReaction(liveReaction);
			});
		},

		addLiveReactionListener: function (callback) {
			StoreFactory.getLiveReactionsStore().then((store) => {
				store.addEventListener(store.EVENTS.liveReaction, callback);
			});
		},

		removeLiveReactionListener: function (callback) {
			StoreFactory.getLiveReactionsStore().then((store) => {
				store.removeEventListener(store.EVENTS.liveReaction, callback);
			});
		},
	},
	ClashOfCams: {
		getClashOfCamsData: function (clashId, callback) {
			StoreFactory.getClashOfCamsStore().then((store) => store.getClashOfCamsData(clashId, callback));
		},
		addClashOfCamsVote: function (challengeId, teamId, callback) {
			StoreFactory.getClashOfCamsStore().then((store) => store.addClashOfCamsVote(challengeId, teamId, callback));
		},
		removeClashOfCamsVote: function (challengeId, callback) {
			StoreFactory.getClashOfCamsStore().then((store) => store.removeClashOfCamsVote(challengeId, callback));
		},
		addClashOfCamsVoteListener: function (callback) {
			StoreFactory.getClashOfCamsStore().then((store) => store.addEventListener(store.EVENTS.vote, callback));
		},
		removeClashOfCamsVoteListener: function (callback) {
			StoreFactory.getClashOfCamsStore().then((store) => store.removeEventListener(store.EVENTS.vote, callback));
		},
	},
	Spinwheel: {
		getSpinwheelInfo(callback, type = Constants.Spinwheel.Types.default) {
			StoreFactory.getSpinwheelStore().then((store) => store.getSpinwheelInfo(callback, type));
		},
		doSpinwheelSpin(callback, type = Constants.Spinwheel.Types.default) {
			StoreFactory.getSpinwheelStore().then((store) => store.doSpinwheelSpin(callback, type));
		},
		redeemLoyaltyPointsReward: function (id, callback) {
			StoreFactory.getSpinwheelStore().then((store) => store.redeemLoyaltyPointsReward(id, callback));
		},
		tryMigrate: function (callback) {
			StoreFactory.getSpinwheelStore().then((store) => store.tryLoyaltyPointsMigrate(callback));
		},
		claimCoins: function (callback) {
			StoreFactory.getSpinwheelStore().then((store) => store.claimCoins(callback));
		},
	},
	SearchResult: {
		addTVSeriesResultChangeListener: function (callback) {
			StoreFactory.getSearchResultStore().then((store) => store.addEventListener(store.EVENTS.SeriesResult, callback));
		},
		getSearchResultsByType: function (searchPattern, type, sort, order) {
			return StoreFactory.getSearchResultStore().then((store) => store.getSearchResultsByType(searchPattern, type, sort, order));
		},

		getSearchResultsCurrentCount: function (searchPattern, searchType, sort, order) {
			return StoreFactory.getSearchResultStore().then((store) => store.getSearchResultsCurrentCount(searchPattern, searchType, sort, order));
		},

		getSearchResultsTotalCount: function (searchPattern, searchType, sort, order) {
			return StoreFactory.getSearchResultStore().then((store) => store.getSearchResultsTotalCount(searchPattern, searchType, sort, order));
		},

		setSearchPatternByType: function (searchPattern, searchType, sort, order) {
			StoreFactory.getSearchResultStore().then((store) => store.setSearchPatternByType(searchPattern, searchType, sort, order));
		},
	},
	Mediathek: {
		onTileExpand(id) {
			StoreFactory.getMediathekStore().then((store) => store.onTileExpand(id));
		},
		addTileExpandListener(callback) {
			StoreFactory.getMediathekStore().then((store) => store.addEventListener(store.EVENTS.tileExpandChange, callback));
		},
		removeTileExpandListener(callback) {
			StoreFactory.getMediathekStore().then((store) => store.removeEventListener(store.EVENTS.tileExpandChange, callback));
		},
		getBoughtVideos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getBoughtVideos(first, offset, order, search, callback));
		},
		getPinnedVideos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getPinnedVideos(first, offset, order, search, callback));
		},
		getArchivedVideos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getArchivedVideos(first, offset, order, search, callback));
		},
		getMessengerVideos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getMessengerVideos(first, offset, order, search, callback));
		},
		getClipsVideos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getClipsVideos(first, offset, order, search, callback));
		},
		getFeedVideos(first, after, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getFeedVideos(first, after, callback));
		},
		getBoughtPhotos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getBoughtPhotos(first, offset, order, search, callback));
		},
		getPinnedPhotos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getPinnedPhotos(first, offset, order, search, callback));
		},
		getArchivedPhotos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getArchivedPhotos(first, offset, order, search, callback));
		},
		getMessengerPhotos(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getMessengerPhotos(first, offset, order, search, callback));
		},
		getFeedPhotos(first, after, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getFeedPhotos(first, after, callback));
		},
		getAudio(first, offset, order, search, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getAudio(first, offset, order, search, callback));
		},
		clearMediathekCache() {
			StoreFactory.getMediathekStore().then((store) => store.clearMediathekCache());
		},
		triggerReload(containerId) {
			StoreFactory.getMediathekStore().then((store) => store.triggerReload(containerId));
		},
		addReloadListener(callback) {
			StoreFactory.getMediathekStore().then((store) => store.addEventListener(store.EVENTS.reloadData, callback));
		},
		removeReloadListener(callback) {
			StoreFactory.getMediathekStore().then((store) => store.removeEventListener(store.EVENTS.reloadData, callback));
		},
		getTotals: function (callback) {
			StoreFactory.getMediathekStore().then((store) => store.getTotals(callback));
		},
		archiveContent: function (id, callback) {
			StoreFactory.getMediathekStore().then((store) => store.archiveContent(id, callback));
		},
		unarchiveContent: function (id, callback) {
			StoreFactory.getMediathekStore().then((store) => store.unarchiveContent(id, callback));
		},
		getPlaylists(args, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getPlaylists(args, callback));
		},
		getPlaylistsSummary(args, callback) {
			StoreFactory.getMediathekStore().then((store) => store.getPlaylistsSummary(args, callback));
		},
		renamePlaylist(playlistId, title, callback) {
			StoreFactory.getMediathekStore().then((store) => store.renamePlaylist(playlistId, title, callback));
		},
		deletePlaylist(playlistId, callback) {
			StoreFactory.getMediathekStore().then((store) => store.deletePlaylist(playlistId, callback));
		},
		createPlaylist(title, albumId, callback) {
			StoreFactory.getMediathekStore().then((store) => store.createPlaylist(title, albumId, callback));
		},
		editPlaylist(playlistId, albumIds, callback) {
			StoreFactory.getMediathekStore().then((store) => store.editPlaylist(playlistId, albumIds, callback));
		},
		addAlbumToPlaylist(playlistId, albumId, callback) {
			StoreFactory.getMediathekStore().then((store) => store.addAlbumToPlaylist(playlistId, albumId, callback));
		},
		removeAlbumFromPlaylist(playlistId, albumId, callback) {
			StoreFactory.getMediathekStore().then((store) => store.removeAlbumFromPlaylist(playlistId, albumId, callback));
		},
		reloadPlaylist(playlistIds, showExpand = false) {
			StoreFactory.getMediathekStore().then((store) => store.reloadPlaylist(playlistIds, showExpand));
		},
		addPlaylistChangeListener(callback) {
			StoreFactory.getMediathekStore().then((store) => store.addEventListener(store.EVENTS.playlistChange, callback));
		},
		removePlaylistChangeListener(callback) {
			StoreFactory.getMediathekStore().then((store) => store.removeEventListener(store.EVENTS.playlistChange, callback));
		},
		isVideoInPlaylist(albumId, callback) {
			StoreFactory.getMediathekStore().then((store) => store.isVideoInPlaylist(albumId, callback));
		},
	},
	Achievement: {
		getPrestigeInfo: function (callback, useCache = true) {
			StoreFactory.getAchievementStore().then((store) => store.getPrestigeInfo(callback, useCache));
		},
		redeemReward: function (levelId, callback) {
			StoreFactory.getAchievementStore().then((store) => store.redeemReward(levelId, callback));
		},
		getAchievement: function (id, callback) {
			StoreFactory.getAchievementStore().then((store) => store.getAchievement(id, callback));
		},
		getAchievements: function (inProgress, callback, useCache = true) {
			StoreFactory.getAchievementStore().then((store) => store.getAchievements(inProgress, callback, useCache));
		},
		getOwnedCollectibles: function (callback, useCache = true) {
			StoreFactory.getAchievementStore().then((store) => store.getOwnedCollectibles(callback, useCache));
		},
		getSelectedFrame: function (callback, useCache = true) {
			StoreFactory.getAchievementStore().then((store) => store.getSelectedFrame(callback, useCache));
		},
		getSelectedBadges: function (callback, useCache = true) {
			StoreFactory.getAchievementStore().then((store) => store.getSelectedBadges(callback, useCache));
		},
		selectFrame: function (frameId, callback) {
			StoreFactory.getAchievementStore().then((store) => store.selectFrame(frameId, callback));
		},
		selectBadges: function (badgeIds, callback) {
			StoreFactory.getAchievementStore().then((store) => store.selectBadges(badgeIds, callback));
		},
		claimReward: function (achievementId, levelNumber, callback) {
			StoreFactory.getAchievementStore().then((store) => store.claimReward(achievementId, levelNumber, callback));
		},
		initAchievements: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.initAchievements(callback));
		},
		triggerReloadAchievements: function () {
			StoreFactory.getAchievementStore().then((store) => store.triggerReloadAchievements());
		},
		addReloadAchievementsListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.addReloadAchievementsListener(callback));
		},
		removeReloadAchievementsListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.removeReloadAchievementsListener(callback));
		},
		triggerReloadCollectibles: function () {
			StoreFactory.getAchievementStore().then((store) => store.triggerReloadCollectibles());
		},
		addReloadCollectiblesListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.addReloadCollectiblesListener(callback));
		},
		removeReloadCollectiblesListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.removeReloadCollectiblesListener(callback));
		},
		triggerReloadPrestigeInfo: function () {
			StoreFactory.getAchievementStore().then((store) => store.triggerReloadPrestigeInfo());
		},
		addReloadPrestigeInfoListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.addReloadPrestigeInfoListener(callback));
		},
		removeReloadPrestigeInfoListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.removeReloadPrestigeInfoListener(callback));
		},
		triggerReloadSelectedFrame: function () {
			StoreFactory.getAchievementStore().then((store) => store.triggerReloadSelectedFrame());
		},
		addReloadSelectedFrameListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.addReloadSelectedFrameListener(callback));
		},
		removeReloadSelectedFrameListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.removeReloadSelectedFrameListener(callback));
		},
		triggerReloadSelectedBadges: function () {
			StoreFactory.getAchievementStore().then((store) => store.triggerReloadSelectedBadges());
		},
		addReloadSelectedBadgesListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.addReloadSelectedBadgesListener(callback));
		},
		removeReloadSelectedBadgesListener: function (callback) {
			StoreFactory.getAchievementStore().then((store) => store.removeReloadSelectedBadgesListener(callback));
		},
	},
	Survey: {
		addSurveyChangeListener: function (callback) {
			StoreFactory.getSurveyStore().then((store) => store.addEventListener(store.EVENTS.Survey, callback));
		},

		removeSurveyChangeListener: function (callback) {
			StoreFactory.getSurveyStore().then((store) => store.removeEventListener(store.EVENTS.Survey, callback));
		},

		addSurveyActiveChangeListener: function (callback) {
			StoreFactory.getSurveyStore().then((store) => store.addEventListener(store.EVENTS.SurveyActive, callback));
		},

		removeSurveyActiveChangeListener: function (callback) {
			StoreFactory.getSurveyStore().then((store) => store.removeEventListener(store.EVENTS.SurveyActive, callback));
		},

		getActiveSurveyType: function () {
			return StoreFactory.getSurveyStore().then((store) => store.getActiveSurveyType());
		},

		getSurveyData: function (type) {
			return StoreFactory.getSurveyStore().then((store) => store.getSurveyData(type));
		},

		loadFeedbackData: function () {
			StoreFactory.getSurveyStore().then((store) => store.loadFeedbackData());
		},

		storeChatRating: function (chatId, actorId, rating, message, isMobile, isAnonymousVoicecall, chatType) {
			StoreFactory.getSurveyStore().then((store) =>
				store.storeChatRating(chatId, actorId, rating, message, isMobile, isAnonymousVoicecall, chatType),
			);
		},

		storeFeedbackAnswer: function (isMobile, feedback, email) {
			StoreFactory.getSurveyStore().then((store) => store.storeFeedbackAnswer(isMobile, feedback, email));
		},
	},
};

Flux.ChatExitCodes = ChatExitCodes;
Flux.Constants = Constants;

window.Flux = Flux;

export default Flux;
